import { useQuery } from '@tanstack/react-query'
import { AlertCircle, CheckCircle2, Clock, Database, ExternalLink, HardDrive, Server } from 'lucide-react'
import { Link } from 'react-router-dom'

import { dbConsoleService, formatBytes, type DatabaseHealth } from '@/services/dbConsole'

function metricValue(value: string | null | undefined, empty = 'Not reported'): { text: string; live: boolean } {
  if (value === null || value === undefined || value === '') {
    return { text: empty, live: false }
  }
  return { text: value, live: true }
}

function healthMetrics(data: DatabaseHealth | undefined) {
  if (!data) return []
  const rows = data.totals.rows
  return [
    {
      label: 'Rows stored',
      value: metricValue(rows === null || rows === undefined ? null : rows.toLocaleString('en-US')),
      icon: HardDrive,
    },
    {
      label: 'Size on disk',
      value: metricValue(data.engine.sizeBytes == null ? null : formatBytes(data.engine.sizeBytes)),
      icon: Server,
    },
    {
      label: 'Engine',
      value: metricValue(data.engine.version ?? data.engine.dialect ?? null),
      icon: Database,
    },
    {
      label: 'Health',
      value: {
        text: data.healthy ? 'Healthy' : 'Attention required',
        live: true,
        ok: data.healthy,
      },
      icon: CheckCircle2,
    },
  ]
}

export default function DatabaseSettingsTab() {
  const query = useQuery({
    queryKey: ['db-console', 'health'],
    queryFn: dbConsoleService.health,
    retry: false,
  })

  const metrics = healthMetrics(query.data)
  const measured = query.data
    ? new Date(
        query.data.checkedAt.endsWith('Z') || query.data.checkedAt.includes('+')
          ? query.data.checkedAt
          : `${query.data.checkedAt}Z`,
      )
    : null

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-[13px] font-semibold text-stone-100">Database Status</h3>
        <p className="max-w-3xl text-[11px] leading-relaxed text-stone-500">
          Figures come from the live database this deployment is connected to. A value the engine
          cannot answer is shown as not reported — never as a plausible number.
        </p>
      </div>

      {query.isError ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          <AlertCircle size={14} />
          Could not load database health. Confirm you have Database Console access.
        </div>
      ) : null}

      {query.isLoading ? (
        <div className="text-[12px] text-stone-500">Reading live database health…</div>
      ) : null}

      {metrics.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.label} className="metric-cell flex items-start gap-3">
              <m.icon size={16} className="mt-0.5 flex-shrink-0 text-amber-400/60" />
              <div>
                <div className="metric-cell-label">{m.label}</div>
                <div
                  className={`metric-cell-value text-[15px] ${
                    'ok' in m.value && m.value.ok === false
                      ? 'text-red-300'
                      : m.value.live
                        ? 'ok' in m.value && m.value.ok
                          ? 'text-emerald-400'
                          : ''
                        : 'text-stone-500'
                  }`}
                >
                  {m.value.text}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {measured && !Number.isNaN(measured.getTime()) ? (
        <p className="text-[10px] text-stone-600">Measured {measured.toLocaleString()} against this deployment.</p>
      ) : null}

      <div className="h-px bg-amber-400/10" />

      <div>
        <h3 className="mb-1 text-[13px] font-semibold text-stone-100">Backups</h3>
        <p className="mb-3 max-w-3xl text-[11px] leading-relaxed text-stone-500">
          This platform does not run an in-app backup scheduler. Azure / ops backups are configured
          on the host, not from this screen.
        </p>
        <div className="rounded-lg border border-amber-400/10 bg-black/30">
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-amber-500/20 bg-amber-500/5">
              <Clock size={20} className="text-amber-300/80" />
            </div>
            <div className="text-[13px] font-medium text-stone-300">No in-app backup schedule</div>
            <p className="max-w-sm text-[11.5px] leading-relaxed text-stone-500">
              There is no backup catalog stored here. Invented last-run rows would look like live
              protection. Host backups are the operator path.
            </p>
          </div>
        </div>
      </div>

      <div className="h-px bg-amber-400/10" />

      <div>
        <h3 className="mb-1 text-[13px] font-semibold text-stone-100">Export</h3>
        <p className="mb-3 max-w-3xl text-[11px] leading-relaxed text-stone-500">
          Bulk export requires a request and a second approver. It is not a one-click download from
          this page.
        </p>
        <Link
          to="/db-search"
          className="btn-secondary inline-flex items-center gap-2 text-[12px]"
        >
          <ExternalLink size={14} /> Open Database Console (health, ledger, dual-auth export)
        </Link>
      </div>
    </div>
  )
}
